'use client'

import { cn } from '@/utilities/cn'
import { createUrl } from '@/utilities/createUrl'
import { SearchIcon, Loader2, X } from 'lucide-react'
import { Link, useRouter, usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Product } from '@/payload-types'
import { Price } from '@/components/Price'
import { useTranslations, useLocale } from 'next-intl'

type Props = {
  className?: string
  onSelect?: () => void
}

export const Search: React.FC<Props> = ({ className, onSelect }) => {
  const t = useTranslations('search')
  const locale = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [query, setQuery] = useState(searchParams?.get('q') || '')
  const [results, setResults] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const fetchSuggestions = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.trim().length < 2) {
        setResults([])
        return
      }

      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          'where[title][like]': searchQuery.trim(),
          limit: '5',
          depth: '1',
        })

        const response = await fetch(`/api/products?${params.toString()}`)
        if (response.ok) {
          const data = await response.json()
          setResults(data.docs || [])
        }
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [locale],
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim()) {
        fetchSuggestions(query)
      } else {
        setResults([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, fetchSuggestions])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const newParams = new URLSearchParams(searchParams?.toString())

    if (query) {
      newParams.set('q', query)
    } else {
      newParams.delete('q')
    }

    setShowSuggestions(false)
    onSelect?.()
    router.push(createUrl('/shop', newParams))
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setShowSuggestions(false)
    const newParams = new URLSearchParams(searchParams?.toString())
    newParams.delete('q')
    router.push(createUrl('/shop', newParams))
  }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <form onSubmit={onSubmit} className="relative">
        <input
          autoComplete="off"
          className="w-full rounded-2xl border border-white/25 bg-black/40 backdrop-blur-xl px-6 py-3 text-sm font-mono tracking-widest uppercase text-white placeholder:text-neutral-400 placeholder:uppercase transition-all shadow-[0_4px_20px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.1)] focus:bg-black/50 focus:border-[var(--neon-orange)]/60 focus:ring-2 focus:ring-[var(--neon-orange)]/30 focus:shadow-[0_0_25px_-5px_var(--neon-orange)] outline-none pr-14"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          name="search"
          placeholder={t('placeholder')}
          type="text"
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {query ? (
            <button
              onClick={handleClear}
              type="button"
              className="group transition-colors"
              aria-label="Clear search"
            >
              <X className="h-5 w-5 text-neutral-400 group-hover:text-[var(--neon-orange)] transition-colors" />
            </button>
          ) : isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          ) : (
            <SearchIcon className="h-5 w-5 text-neutral-400" />
          )}
        </div>
      </form>

      {showSuggestions && (results.length > 0 || isLoading) && (
        <div className="absolute top-full mt-2 w-full overflow-hidden rounded-2xl border border-white/20 bg-black/60 backdrop-blur-2xl shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50">
          {isLoading && results.length === 0 ? (
            <div className="p-4 text-center text-sm text-neutral-400">{t('searching')}</div>
          ) : (
            <ul className="py-2">
              {results.map((product) => (
                <li key={product.id}>
                  <Link
                    href={`/products/${product.slug}`}
                    onClick={() => {
                      setIsNavigating(true)
                      setShowSuggestions(false)
                      onSelect?.()
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-neutral-200 group-hover:text-[var(--neon-orange)] transition-colors">
                        {product.title}
                      </p>
                      {typeof product.basePrice === 'number' && (
                        <p className="text-xs text-neutral-400">
                          <Price amount={product.basePrice} as="span" />
                        </p>
                      )}
                    </div>
                    <div className="text-[var(--neon-orange)] opacity-0 group-hover:opacity-100 transition-opacity text-xs font-mono">
                      {t('view')} →
                    </div>
                  </Link>
                </li>
              ))}
              <li className="border-t border-white/10 mt-2">
                <button
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams?.toString())
                    newParams.set('q', query)
                    router.push(createUrl('/shop', newParams))
                    setShowSuggestions(false)
                    onSelect?.()
                  }}
                  className="w-full px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-400 hover:text-[var(--neon-orange)] transition-colors"
                >
                  {t('seeAll', { query })}
                </button>
              </li>
            </ul>
          )}
        </div>
      )}

      {/* Navigation loading overlay */}
      {isNavigating && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 animate-spin text-[var(--neon-orange)]" />
            <p className="text-sm font-mono text-white/70">Loading...</p>
          </div>
        </div>
      )}
    </div>
  )
}
